import { type OperationVariables } from '@apollo/react-common';
import { addTypenameToDocument } from 'apollo-utilities';
import { createOperationDescriptor, getRequest } from 'relay-runtime';

import { toDocumentNode } from '../../toDocumentNode.ts';
import {
	type CacheBridgeCache,
	getFailureContext,
	getSecondaryCache,
	skipped,
	writeSafely,
	written,
} from '../cacheBridgeCommon.ts';
import { addRelayConcreteTypenamesToOperationData } from '../queryResultTypenames.ts';
import { getApolloClient } from '../utils/getApolloClient.ts';
import { getRelayEnvironment } from '../utils/getRelayEnvironment.ts';
import { shouldSkipCacheBridgeSync } from '../utils/shouldSkipCacheBridgeSync.ts';

import {
	type CacheWriteResult,
	type MutationCacheBridgeStageArgs,
	type MutationCacheBridgeWriteArgs,
} from './mutationCacheBridgeTypes.ts';

function writeMutationToApollo<
	TData,
	TVariables extends Record<string, unknown> = OperationVariables,
>(args: MutationCacheBridgeWriteArgs<TData, TVariables>): CacheWriteResult {
	const skipReason = shouldSkipCacheBridgeSync(args);
	if (skipReason) return skipped(skipReason);

	const client = getApolloClient({ useAggClient: args.useAggClient });
	if (!client?.cache?.write) return skipped('apollo-client-missing');

	return writeSafely(
		() => {
			const data = addRelayConcreteTypenamesToOperationData(args.mutation, args.data as TData);

			// Apollo Client 2 writes mutation payloads at ROOT_MUTATION. This mirrors
			// Apollo's native mutation cache path while still allowing normalized child
			// entities with stable id/__typename values to be shared with Apollo readers.
			client.cache.write({
				query: addTypenameToDocument(toDocumentNode(args.mutation)),
				variables: args.variables,
				dataId: 'ROOT_MUTATION',
				result: data,
			});
			return written();
		},
		getFailureContext('mutation', 'write', 'apollo'),
	);
}

function writeMutationToRelay<
	TData,
	TVariables extends Record<string, unknown> = OperationVariables,
>(args: MutationCacheBridgeWriteArgs<TData, TVariables>): CacheWriteResult {
	const skipReason = shouldSkipCacheBridgeSync(args);
	if (skipReason) return skipped(skipReason);

	const environment = getRelayEnvironment();
	if (!environment) return skipped('relay-environment-missing');

	return writeSafely(
		() => {
			// Relay can normalize a mutation response with the same operation descriptor
			// used for the original mutation, so the bridge only commits the successful
			// payload and lets Relay own store normalization.
			const operation = createOperationDescriptor(getRequest(args.mutation), args.variables ?? {});
			environment.commitPayload(operation, args.data as Record<string, unknown>);
			return written();
		},
		getFailureContext('mutation', 'write', 'relay'),
	);
}

function writeMutationToCache<
	TData,
	TVariables extends Record<string, unknown> = OperationVariables,
>(args: MutationCacheBridgeWriteArgs<TData, TVariables>, targetCache: CacheBridgeCache) {
	return targetCache === 'apollo' ? writeMutationToApollo(args) : writeMutationToRelay(args);
}

export function syncMutationPayload<
	TData,
	TVariables extends Record<string, unknown> = OperationVariables,
>({ source, ...writeArgs }: MutationCacheBridgeStageArgs<TData, TVariables>): CacheWriteResult {
	return writeMutationToCache(writeArgs, getSecondaryCache(source));
}
